// REACT
import { useEffect, useState } from 'react';
// YARN
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, InputLabel } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import { Select as SelectMui } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
// SERVICES
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createGroupementParcel, editGroupementParcel } from 'services/API/Exploitation';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
// THEMES
import { ThemeProvider } from '@mui/material/styles';
import '../../../assets/styles/group_parcel.style.scss';
import {
  customDialogRegroupParcelsTheme,
  SelectParcelTheme,
} from 'assets/styles/themes/Dialog/generic_dialog_regroup_parcel';
import LoadingScreen from 'components/generics/loadingScreen';
import {
  getCultureCampaignKey,
  filterParcelFromActivityDate,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { displayErrorMessage, sortByLocalName } from 'utils/tools_functions';

const DEFAULT_FORM = { parent: '', children: [] };

const GroupParcelModal = ({
  onSubmitSuccess,
  loadingComplete,
  allParcels,
  exploitation,
  edit,
  modalGroup,
  setModalGroup,
}) => {
  const [formData, setFormData] = useState(edit || DEFAULT_FORM);
  const [childrenParcels, setChildrenParcels] = useState([]);
  const [parcelsWithEmptyCultures, setParcelsWithEmptyCultures] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (allParcels?.length) {
      const activeParcels = filterParcelFromActivityDate(
        allParcels,
        exploitation?.forecastCampaign,
      );
      const catchParcelsEmptyCultures = activeParcels.map((parcel) => {
        if (!parcel.parcelCultures[getCultureCampaignKey(1, exploitation?.forecastCampaign)]) {
          parcel = {
            ...parcel,
            previousCulture1: {
              id: 0,
              name: t('column.cultureNEmpty'),
              campaign: { formatString: '', startYear: 0, endYear: 0 },
              surface: 0,
              irrigation: 0,
              type: 'main',
              cover: 'POSSIBLE',
              isSplit: false,
              organicState: 'conventional',
            },
          };
        }
        if (!parcel.parcelCultures[getCultureCampaignKey(2, exploitation?.forecastCampaign)]) {
          parcel = {
            ...parcel,
            previousCulture2: {
              id: 0,
              name: t('column.cultureN1Empty'),
              campaign: { formatString: '', startYear: 0, endYear: 0 },
              surface: 0,
              irrigation: 0,
              type: 'main',
              cover: 'POSSIBLE',
              isSplit: false,
              organicState: 'conventional',
            },
          };
        }
        return { ...parcel };
      });
      setParcelsWithEmptyCultures(catchParcelsEmptyCultures);
    }
  }, [allParcels, exploitation?.forecastCampaign]);

  const getParcelLabel = (parcel) => {
    return `${parcel?.name} ( ${parcel?.surface} ha - ${
      parcel?.parcelCultures?.[getCultureCampaignKey(1, exploitation?.forecastCampaign)]?.name ??
      t('column.cultureNEmpty')
    } - ${
      parcel?.parcelCultures?.[getCultureCampaignKey(2, exploitation?.forecastCampaign)]?.name ??
      t('column.cultureN1Empty')
    } - ${parcel?.groundType?.name})`;
  };

  const getParcelLabelById = (id) => {
    const parcel = parcelsWithEmptyCultures.find((p) => p.id === id);
    return parcel ? getParcelLabel(parcel) : '';
  };

  const getOptionParent = () => {
    let optionsParents;
    if (!edit) {
      optionsParents = parcelsWithEmptyCultures.filter((parcel) => {
        const isChildParcel = parcel?.parent ? true : false;
        const isParentParcel = parcel?.isParent ? true : false;

        if (isChildParcel) {
          return false;
        }
        if (isParentParcel) {
          return false;
        }

        return true;
      });
    } else {
      optionsParents = parcelsWithEmptyCultures.filter(
        (parcel) => parcel?.isParent && parcel?.id === edit?.parent,
      );
    }
    optionsParents.forEach((parcel, index) => {
      optionsParents[index].label = getParcelLabel(parcel);
    });

    return [...optionsParents];
  };

  useEffect(() => {
    if (!!formData.parent) {
      const parentParcel = parcelsWithEmptyCultures.find(
        (parcel) => parcel.id === +formData.parent,
      );
      let allChildrenParcels = parcelsWithEmptyCultures
        .filter(
          (parcel) =>
            !parcel?.isParent &&
            (!parcel?.parent || parcel.parent === +formData?.parent) &&
            parcel?.id !== +formData?.parent &&
            parcel?.organicState === parentParcel?.organicState,
        )
        .map((parcel) => ({ ...parcel, label: getParcelLabel(parcel) }));
      setChildrenParcels([...allChildrenParcels]);
    } else {
      setChildrenParcels([]);
    }
  }, [formData?.parent, parcelsWithEmptyCultures]);

  const updateParent = (e) => {
    let clone = JSON.parse(JSON.stringify(formData));
    clone.parent = +e;
    let children = allParcels.reduce((accumulator, currentParcel) => {
      if (currentParcel?.parent && currentParcel?.parent === e) {
        accumulator.push(currentParcel.id);
      }
      return accumulator;
    }, []);
    clone.children = children;
    setFormData(clone);
  };

  const deleteChild = (index) => {
    let newFormData = { ...formData };
    newFormData.children = newFormData.children.filter(
      (childrenId) => childrenId !== newFormData.children[index],
    );
    setFormData(newFormData);
  };

  const onSubmit = (e) => {
    if (formData.children.length > 0) {
      setModalGroup(false);
      if (e) e.preventDefault();

      if (!edit) {
        createGroupementParcel(exploitation.id, formData)
          .then(() => {
            toast.success(t('success.groupingCreated'));
            setFormData(DEFAULT_FORM);
            updateParcelWithoutAPI();
            onSubmitSuccess();
          })
          .catch((err) => toast.error(displayErrorMessage(err?.response?.data?.code)));
      } else {
        editGroupementParcel(exploitation.id, formData, edit.parent)
          .then(() => {
            toast.success(t('success.groupingUpdated'));
            setFormData(DEFAULT_FORM);
            updateParcelWithoutAPI();
            onSubmitSuccess();
          })
          .catch((err) => toast.error(displayErrorMessage(err?.response?.data?.code)));
      }
    }
  };

  const updateParcelWithoutAPI = () => {
    allParcels[allParcels.indexOf(allParcels.filter((p) => p.id === formData.parent)[0])].isParent =
      true;
    formData.children.forEach((c) => {
      allParcels[allParcels.indexOf(allParcels.filter((p) => p.id === c)[0])].parent =
        +formData.parent;
    });
  };

  const renderValue = () => {
    const multiSelectLabels = formData?.children.map((parcelId) => getParcelLabelById(parcelId));

    return <p> {multiSelectLabels.join(', ')} </p>;
  };

  const renderMainParcel = () => {
    const mainParcel = getOptionParent()?.find((parcel) => parcel.id === formData?.parent)?.label;

    return <p> {mainParcel} </p>;
  };

  const handleChange = (value) => {
    let newFormData = { ...formData };

    if (value[value.length - 1] === 'all-parcels') {
      if (newFormData.children.length === childrenParcels.length) {
        newFormData.children = [];
      } else {
        newFormData.children = childrenParcels.map((child) => child.id);
      }
    } else {
      if (value.every((childOptionValue) => typeof childOptionValue === 'number')) {
        newFormData.children = value;
      }
    }

    setFormData(newFormData);
  };

  return (
    <ThemeProvider theme={customDialogRegroupParcelsTheme}>
      <Dialog maxWidth="md" fullWidth open={modalGroup} onClose={() => setModalGroup(false)}>
        <LoadingScreen open={!loadingComplete} />
        <Box>
          <DialogTitle>{t('title.groupParcels')}</DialogTitle>
          <ClearIcon onClick={() => setModalGroup(false)} />
        </Box>

        <DialogContent className="dialogcontent-select-parcel">
          <InputLabel id="MainParcel">{t('label.mainParcel')}</InputLabel>
          <FormControl sx={{ width: '100%' }} variant="filled">
            <ThemeProvider theme={SelectParcelTheme}>
              <InputLabel id="selectParcel">{t('message.chooseMainParcel')}</InputLabel>
              <SelectMui
                sx={{ height: 50 }}
                labelId="selectParcel"
                className="form"
                id="selectMainParcel"
                value={formData?.parent}
                onChange={(event) => updateParent(event.target.value)}
                input={<OutlinedInput label="Tag" />}
                renderValue={renderMainParcel}
                MenuProps={{ autoFocus: false }}
              >
                {sortByLocalName(getOptionParent()).map((parcel) => (
                  <MenuItem key={parcel?.id} value={parcel?.id}>
                    <ListItemText primary={parcel.label} />
                  </MenuItem>
                ))}
              </SelectMui>
            </ThemeProvider>
          </FormControl>
        </DialogContent>

        <DialogContent className="dialogcontent-select-parcel">
          <InputLabel id="childrenParcels">{t('label.childParcels')}</InputLabel>
          <FormControl sx={{ width: '100%' }} variant="filled">
            <ThemeProvider theme={SelectParcelTheme}>
              <InputLabel id="selectChildrenParcel">
                {!formData.parent
                  ? t('message.chooseMainParcelFirst')
                  : childrenParcels?.length === 0
                    ? t('message.noParcelsAssociable')
                    : childrenParcels?.length === formData?.children?.length
                      ? t('message.moreParcelsAssociable')
                      : t('message.chooseChildParcels')}
              </InputLabel>
              <SelectMui
                sx={{ height: 50 }}
                labelId="selectChildrenParcel"
                id="multiSelectParcel"
                multiple
                value={formData?.children}
                onChange={(event) => handleChange(event.target.value)}
                input={<OutlinedInput label="Tag" />}
                renderValue={renderValue}
                MenuProps={{ autoFocus: false }}
              >
                {childrenParcels.length && (
                  <MenuItem key={'select-all-parcels'} value={'all-parcels'}>
                    <Checkbox checked={childrenParcels.length === formData?.children.length} />
                    <ListItemText primary={t('common.selectAll')} />
                  </MenuItem>
                )}
                {childrenParcels.length ? (
                  sortByLocalName(childrenParcels).map((child) => (
                    <MenuItem key={child.id} value={child.id}>
                      <Checkbox checked={formData?.children?.indexOf(child.id) > -1} />
                      <ListItemText primary={child.label} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem key="noChildrenKey" disabled>
                    <ListItemText primary={t('message.noParcelsAssociable')} />
                  </MenuItem>
                )}
              </SelectMui>
            </ThemeProvider>
          </FormControl>
        </DialogContent>

        <DialogContent
          className="group-parcel-list"
          sx={{
            minHeight: formData?.children?.length > 0 ? '132px' : '0px',
            height:
              formData?.children?.length > 0
                ? 'calc(' + formData?.children?.length + '*46px)'
                : '0px',
          }}
        >
          {formData?.children?.length > 0 ? (
            <div className="parcel-list__wrapper">
              <List className="parcel-list__list">
                {formData?.children?.map((childId, index) => (
                  <ListItem
                    key={index}
                    className="parcel-list__item"
                    secondaryAction={
                      <div key={index} className="button_delete" onClick={() => deleteChild(index)}>
                        <ClearIcon fontSize="medium" htmlColor="white" />
                      </div>
                    }
                  >
                    <ListItemText
                      key={index}
                      className="parcel-list__item"
                      primary={getParcelLabelById(+childId)}
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ) : (
            <div></div>
          )}
        </DialogContent>

        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="inputCostButton" onClick={onSubmit} disabled={formData.children.length < 1}>
              {edit ? t('common.save') : t('button.validateGrouping')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default GroupParcelModal;
